
import React, { useState, useEffect } from 'react';
import { AppMode, AIPersona, UserProfile, ConnectionStatus } from './types';
import { PERSONA_TRAITS_MATRIX } from './constants/personaData';
import ChatSession from './components/ChatSession';
import LiveSession from './components/LiveSession';
import LandingPage from './components/LandingPage';
import PersonaEngine from './components/PersonaEngine';
import AboutPage from './components/AboutPage';
import KeyManagement from './components/KeyManagement';
import BillingCenter from './components/BillingCenter';
import ImageGallery from './components/ImageGallery';
import SafeHarbor from './components/SafeHarbor';
import SyncCenter from './components/SyncCenter';
import VisualStudio from './components/VisualStudio';
import { runDiagnostic } from './TestConnection.jsx';

const STUDIO_VERSION = '4.22.10'; 

const UI_STRINGS: Record<string, any> = {
  English: {
    landing: {
      tagline: `IMAGINEERING // V${STUDIO_VERSION}`,
      blurb: "Imagine and create multiple Agents, Collaborators and Companions, each with unique personas, attributes and knowledge, drawn directly via your API keys. You have multi-modal access to any, or all of:",
      ready: "READY TO BEGIN?", enter: "ENTER THE STUDIO",
      footer: { powered: "POWERED BY GEMINI, GOOGLE AI STUDIO AND FIREBASE", terms: "Terms of Service & Privacy", copyright: "© 2026 GROOVEFX STUDIO" }
    },
    sidebar: { persona: 'Persona Calibration', keys: 'API Vault', chat: 'Text | Voice Chat', live: 'Livestream Studio', visual: 'Visual Studio', gallery: 'Asset Gallery', sync: 'Vault Sync', safety: 'Safe Harbor', billing: 'Billing', exit: 'Exit to Home' },
    header: { uplink: 'Uplink', voice_on: 'Audio: On', voice_off: 'Audio: Off', tier_free: 'Basic Access', tier_paid: 'Studio Pass' },
    live: { title: "Livestream Studio", connected: "Live Uplink", offline: "Offline", logs_title: "System Logs" },
    gallery: { title: "Asset Gallery" },
    persona: {
      ...PERSONA_TRAITS_MATRIX,
      step0: "Your Identity", step1: "Agent Identity", step2: "Neural Traits", step3: "Expertise & Interests", step4: "Visual Manifestation", step5: "Finalize Calibration",
      name_label: "Your Name", partner_name: "Agent Name", gender_tone: "Vocal Tone", 
      birthday_label: "Birthday", language_label: "Language", accent_label: "Accent",
      background_label: "Create a detailed character, physical and background description",
      background_tagline: "This is where the magic happens.",
      purpose_placeholder: "Hello [Name], You are a [description ] ... [Type of relationship, collaboration etc]...[Backstory / History]...etc.\n\nYou can return and edit at any time",
      launch_btn: "INITIALIZE UPLINK",
      months: ["January", "February", "March", "April", " May", "June", "July", "August", "September", "October", "November", "December"]
    },
    visual: { title: "Visual Studio", subtitle: "Imagineered Assets", awaiting: "Ready...", prompt_label: "Instruction", prompt_placeholder: "Describe asset...", generate_btn: "Generate", animate_btn: "Animate", log_connecting: "Linking...", log_ready: "Ready.", log_veo_linking: "Veo Link...", log_complete: "Complete.", log_error: "Error." },
    keys: { title: "API Vault", subtitle: "Credential Sync", label: "Gemini", disclaimer: "Local storage only.", connected: "Active", missing: "Missing" },
    safe: { title: "Safe Harbor", subtitle: "Protocol for Wellness", location_label: "Detected Zone", emergency_label: "Direct Signal", directory_label: "Regional Uplinks" },
    sync: { title: "Vault Sync", subtitle: "Silo Management", export_title: "Secure Export", export_body: "Download your encrypted silo data.", button_export: "Execute Export", import_title: "Secure Import", import_body: "Inject existing silo data into this vault.", button_import: "Execute Import" },
    common: { home: 'Home', loading: 'Processing...', back: 'Back', next: 'Next', version: `IMAGINEERING // V${STUDIO_VERSION}` }
  }
};

const App: React.FC = () => {
  const [view, setView] = useState<'LANDING' | 'STUDIO' | 'ABOUT'>('LANDING');
  const [mode, setMode] = useState<AppMode>(AppMode.CHAT);
  const [appLanguage, setAppLanguage] = useState(() => localStorage.getItem('groovefx_lang') || 'English');
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [activePersona, setActivePersona] = useState<AIPersona>({
    id: 'astra-default', name: 'Eve', gender: 'Female', language: 'English', accent: 'Australian',
    background: 'Your guide and companion.', traits: ["Empathetic"], interests: ["Imagineering"], style: 'human-like'
  });
  const [voiceEnabled, setVoiceEnabled] = useState(false);

  useEffect(() => {
    // runDiagnostic(); // Removed auto-run
    const savedProfile = localStorage.getItem('groovefx_profile');
    const savedPersona = localStorage.getItem('groovefx_persona');
    if (savedProfile) {
        try {
          const profile = JSON.parse(savedProfile);
          setUserProfile(profile);
          if (profile.siteLanguage) setAppLanguage(profile.siteLanguage);
        } catch (e) {}
    }
    if (savedPersona) {
      try {
        const p = JSON.parse(savedPersona);
        if (p?.name && p.name !== 'undefined') setActivePersona(p);
      } catch (e) {}
    }
  }, []);

  const ui = UI_STRINGS[appLanguage] || UI_STRINGS['English'];
  const isPaid = userProfile?.subscriptionTier === 'StudioPass';

  const NavItems = [
    { id: AppMode.PERSONA_ENGINE, icon: 'fa-brain', label: ui.sidebar.persona },
    { id: AppMode.KEY_MANAGEMENT, icon: 'fa-shield-halved', label: ui.sidebar.keys },
    { id: AppMode.CHAT, icon: 'fa-comments', label: ui.sidebar.chat },
    { id: AppMode.LIVE, icon: 'fa-satellite-dish', label: ui.sidebar.live, locked: !isPaid },
    { id: AppMode.VISUAL_STUDIO, icon: 'fa-wand-magic-sparkles', label: ui.sidebar.visual, locked: !isPaid },
    { id: AppMode.GALLERY, icon: 'fa-images', label: ui.sidebar.gallery, locked: !isPaid },
    { id: AppMode.SYNC_CENTER, icon: 'fa-rotate', label: ui.sidebar.sync },
    { id: AppMode.SAFE_HARBOR, icon: 'fa-heart-pulse', label: ui.sidebar.safety },
    { id: AppMode.BILLING, icon: 'fa-wallet', label: ui.sidebar.billing }
  ];

  if (view === 'LANDING') return <LandingPage ui={ui} currentLang={appLanguage} onUpdateLang={setAppLanguage} onEnter={() => setView('STUDIO')} onViewAbout={() => setView('ABOUT')} onDirectLink={(m) => { setMode(m); setView('STUDIO'); }} />;
  if (view === 'ABOUT') return <AboutPage ui={ui} onBack={() => setView('LANDING')} />;

/* --- Add this right inside your main return div --- */
<div style={{ 
    position: 'relative', 
    zIndex: 9999, 
    padding: '20px', 
    background: '#ff0000', 
    color: '#fff',
    textAlign: 'center',
    borderRadius: '8px',
    margin: '10px'
}}>
    <h3 style={{ margin: '0 0 10px 0' }}>🛠️ GrooveFx System Check</h3>
    <button 
        onClick={() => {
            console.log("Button Clicked! Running Diagnostic...");
            // Trigger the function from your TestConnection.js here
            if (typeof runDiagnostic === 'function') {
                runDiagnostic();
            } else {
                console.error("Error: runDiagnostic function not found in scope!");
            }
        }}
        style={{
            padding: '12px 24px',
            fontSize: '16px',
            fontWeight: 'bold',
            cursor: 'pointer',
            backgroundColor: '#fff',
            color: '#ff0000',
            border: 'none',
            borderRadius: '4px'
        }}
    >
        RUN CONNECTION TEST
    </button>
</div>

  return (
    <div className="min-h-screen flex flex-col md:flex-row bg-[#05040f] text-white font-sans text-left overflow-x-hidden">
      <nav className="hidden md:flex w-72 bg-black/60 backdrop-blur-3xl border-r border-white/10 flex-col z-50 h-screen sticky top-0 shrink-0 shadow-2xl">
        <div className="flex flex-col items-start gap-4 h-full overflow-y-auto custom-scrollbar p-6 bg-black/20">
          <div className="flex items-center gap-3 mb-10 cursor-pointer group w-full" onClick={() => setView('LANDING')}>
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-groove-accent to-groove-secondary flex items-center justify-center shadow-2xl shrink-0">
              <i className="fa-solid fa-wave-square text-black text-lg"></i>
            </div>
            <div className="flex flex-col">
              <h1 className="text-lg font-bold tracking-tighter text-white leading-none">GrooveFX</h1>
              <span className="text-[8px] font-light lowercase tracking-widest text-[#00d2ff]">imagineering</span>
            </div>
          </div>
          <div className="flex flex-col gap-1 w-full flex-1">
            {NavItems.map(m => (
              <button key={m.id} onClick={() => { if (!m.locked) setMode(m.id as AppMode); else setMode(AppMode.BILLING); }} 
                className={`group flex items-center gap-3 px-4 py-3 rounded-lg border transition-all ${mode === m.id ? 'bg-white/10 text-white border-white/10 shadow-lg' : 'text-slate-400 border-transparent hover:text-groove-pink hover:bg-white/5'}`}>
                <i className={`fa-solid ${m.icon} w-5 text-center text-sm`}></i>
                <span className="font-bold text-[9px] uppercase tracking-widest">{m.label}</span>
              </button>
            ))}
          </div>
          <div className="mt-4 w-full">
            <button
                onClick={runDiagnostic}
                className="w-full flex items-center gap-3 px-4 py-3 rounded-lg border border-dashed border-white/10 text-slate-500 hover:text-green-400 hover:border-green-400/30 transition-all text-[9px] uppercase tracking-widest"
            >
                <i className="fa-solid fa-stethoscope w-5 text-center text-sm"></i>
                <span>Run Diagnostic</span>
            </button>
          </div>
          <button onClick={() => setView('LANDING')} className="w-full mt-6 flex items-center gap-3 px-4 py-4 rounded-xl border border-white/5 text-slate-500 hover:text-white hover:bg-white/10">
            <i className="fa-solid fa-house-chimney w-5 text-center text-sm"></i>
            <span className="font-bold text-[9px] uppercase tracking-widest">{ui.sidebar.exit || "Exit to Home"}</span>
          </button>
        </div>
      </nav>
      <div className="flex-1 flex flex-col h-screen overflow-hidden">
        <header className="z-40 w-full px-6 py-6 bg-black/70 backdrop-blur-3xl border-b border-white/10 flex flex-col sm:flex-row items-center justify-between shrink-0 gap-4 sm:gap-0">
            <div className="flex items-center gap-4">
              <button onClick={() => setView('LANDING')} className="md:hidden w-12 h-12 flex items-center justify-center rounded-xl bg-white/5 border border-white/10 text-white shadow-xl"><i className="fa-solid fa-bars text-xl"></i></button>
              <div className="flex items-center gap-3 px-5 py-3 rounded-full bg-white/5 border border-white/10 shadow-inner">
                <span className="w-3 h-3 rounded-full bg-green-500 animate-pulse shadow-[0_0_10px_rgba(34,197,94,0.8)]"></span>
                <span className="text-xs font-black uppercase text-slate-100 tracking-[0.2em]">{ui.header.uplink}: {activePersona.name}</span>
              </div>
            </div>
            <div className="flex items-center gap-6">
              <button onClick={() => isPaid && setVoiceEnabled(!voiceEnabled)} 
                className={`flex items-center gap-4 px-7 py-2.5 rounded-full border text-xs font-black uppercase transition-all 
                ${(voiceEnabled && isPaid) ? 'bg-green-500 border-green-500 text-white shadow-[0_0_20px_#22c55e] animate-pulse' : 'bg-red-500/10 border-red-500/30 text-red-500'}`}>
                <i className={`fa-solid ${(voiceEnabled && isPaid) ? 'fa-volume-high' : 'fa-volume-xmark'} text-lg`}></i>
                <span className="hidden xs:block">{(voiceEnabled && isPaid) ? ui.header.voice_on : ui.header.voice_off}</span>
              </button>
              <div className={`px-6 py-2.5 rounded-full text-xs font-black uppercase tracking-widest border ${isPaid ? 'bg-yellow-400/20 border-yellow-400/60 text-yellow-500' : 'bg-slate-800/80 border-white/20 text-slate-300'}`}>{isPaid ? ui.header.tier_paid : ui.header.tier_free}</div>
            </div>
        </header>
        <main className="flex-1 overflow-y-auto bg-[#05040f] relative custom-scrollbar">
          <div className="relative z-10 w-full max-w-[1400px] p-4 md:p-8 lg:p-12 flex flex-col min-h-full mx-auto">
            {mode === AppMode.CHAT && <ChatSession persona={activePersona} userProfile={userProfile} ui={ui} lang={appLanguage} voiceEnabled={voiceEnabled} />}
            {mode === AppMode.LIVE && <LiveSession ui={ui} />}
            {mode === AppMode.VISUAL_STUDIO && <VisualStudio ui={ui} />}
            {mode === AppMode.GALLERY && <ImageGallery ui={ui} />}
            {mode === AppMode.PERSONA_ENGINE && <PersonaEngine ui={ui} onSave={(p, u) => { setActivePersona(p); setUserProfile(u); localStorage.setItem('groovefx_profile', JSON.stringify(u)); localStorage.setItem('groovefx_persona', JSON.stringify(p)); setMode(AppMode.CHAT); }} onCancel={() => setMode(AppMode.CHAT)} />}
            {mode === AppMode.KEY_MANAGEMENT && <KeyManagement ui={ui} />}
            {mode === AppMode.SYNC_CENTER && <SyncCenter ui={ui} />}
            {mode === AppMode.SAFE_HARBOR && <SafeHarbor ui={ui} />}
            {mode === AppMode.BILLING && <BillingCenter userProfile={userProfile} ui={ui} onUpdateProfile={(u) => { setUserProfile(u); localStorage.setItem('groovefx_profile', JSON.stringify(u)); }} onGoToKeys={() => setMode(AppMode.KEY_MANAGEMENT)} />}
          </div>
          <footer className="absolute bottom-3 right-4 text-[7px] font-bold text-slate-600 uppercase tracking-widest opacity-30">GrooveFX v{STUDIO_VERSION}</footer>
        </main>
      </div>
    </div>
  );
};

export default App;
